import React, { useState, useEffect, useRef } from "react";
import { Container, Table, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Pagination, PaginationItem, PaginationLink, Label, Row, Col, CardBody, Card } from "reactstrap";
import { getFilteredThreads, getChatUsers, getThreadMessages, translateThread } from "../../API/Backend";
import { TablePagination } from "../../Components/Pagination";
import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "react-flatpickr";
import Skeleton from "react-loading-skeleton";
import { useLocation, useSearchParams } from "react-router-dom";
import { useClient } from "../../Utilities/ClientContext";

export default function ThreadList() {
  const location = useLocation();
  const { activeClient } = useClient();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState("");
  // Text in the searchable user filter; userId is set once it matches a user
  const [userSearch, setUserSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [date, setDate] = useState([]);
  const [sentiment, setSentiment] = useState("");
  const datePickerRef = useRef("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  // Translated text per message (null = shown as is); null when showing originals
  const [translations, setTranslations] = useState(null);
  const [translatedTo, setTranslatedTo] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");
  // Message indexes whose JSON is expanded; JSON starts collapsed
  const [expandedJson, setExpandedJson] = useState(() => new Set());

  const fetchUsers = async () => {
    try {
      const res = await getChatUsers({ clientName: activeClient });
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchThreads = async (page = 1, id = null, sentiment = null, startDate = null, endDate = null, limit = pagination.limit || 10) => {
    setLoading(true);
    try {
      // summary: list without message bodies; they're loaded when a thread is opened
      const query = { page, limit, clientName: activeClient, summary: true };
      if (id) query.userId = id;
      if (sentiment) query.sentiment = sentiment;
      if (startDate) query.startDate = startDate;
      if (endDate) query.endDate = endDate;
      const res = await getFilteredThreads(query);
      setThreads(res.data.data.threads || []);
      setPagination(res.data.data.pagination || {});
    } catch (err) {
      console.error("Error fetching threads:", err);
    }
    setLoading(false);
  };


  // 📦 On first load, and whenever the active client changes
  useEffect(() => {
    if (!activeClient) return;
    fetchUsers();
    const defaultSentiment = searchParams.get('sentiment');
    if(defaultSentiment){
      setSentiment(defaultSentiment);
    }
    else{
      setSentiment("");
    }
    setDate([]);
    setUserId("");
    setUserSearch("");
    fetchThreads(1,null,defaultSentiment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, activeClient]);

  const handlePageChange = (p) => {
    if (pagination.page !== p) {
      const start = date[0] ?? null;
      const end = date[1] ?? null;
      fetchThreads(p, userId, sentiment, start, end, pagination.limit);
      setPagination((prev) => ({ ...prev, page: p }));
    }
  };

  const filterByDate = (dates) => {
    if (dates.length) {
      const s = new Date(dates[0]);
      // Single-day pick has no second date; include the whole end day
      const e = new Date(dates[1] ?? dates[0]);
      e.setHours(23, 59, 59, 999);
      const start = s.toISOString();
      const end = e.toISOString();
      setDate([start, end]);
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchThreads(1, userId, sentiment, start, end, pagination.limit);
    }
    else {
      setDate([]);
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchThreads(1, userId, sentiment, null, null, pagination.limit);
    }
  }

  const sentimentChange = (value) => {
    setSentiment(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchThreads(1, userId, value, date[0] ?? null, date[1] ?? null, pagination.limit);
  }

  const userLabel = (user) =>
    `${user.externalId || user.deviceId || user._id}${user.email ? ` · ${user.email}` : ""}`;

  // Apply the filter when the text matches a user (picked from the list or
  // typed as the exact user/device ID); clearing the box shows all users
  const userSearchChange = (text) => {
    setUserSearch(text);
    const typed = text.trim();
    if (!typed) {
      if (userId) userChange("");
      return;
    }
    const match = users.find(
      (u) => userLabel(u) === text || (u.externalId || u.deviceId || u._id) === typed
    );
    if (match && match._id !== userId) userChange(match._id);
  };

  const userChange = (value) => {
    setUserId(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchThreads(1, value, sentiment, date[0] ?? null, date[1] ?? null, pagination.limit);
  }

  const openModal = async (threadId) => {
    setSelectedThreadId(threadId);
    setTranslations(null);
    setTranslatedTo(null);
    setTranslateError("");
    setExpandedJson(new Set());
    setSelectedMessages([]);
    setModalOpen(true);
    setMessagesLoading(true);
    try {
      const res = await getThreadMessages(threadId);
      setSelectedMessages(res.data.data?.messages || []);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
    setMessagesLoading(false);
  };

  const translateMessages = async (to) => {
    setTranslating(true);
    setTranslateError("");
    try {
      const res = await translateThread(selectedThreadId, to);
      if (!res.data.success) throw new Error(res.data.message);
      setTranslations(res.data.data.translations);
      setTranslatedTo(to);
    } catch (err) {
      setTranslateError(err?.message || "Translation failed, please try again");
    }
    setTranslating(false);
  };

  const showOriginal = () => {
    setTranslations(null);
    setTranslatedTo(null);
  };

  const safeJsonParse = (str) => {
    if (typeof str !== 'string') return { isJson: false, data: str };

    try {
      const trimmed = str.trim();
      // Basic check for JSON object format
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        const parsed = JSON.parse(trimmed);
        return { isJson: true, data: parsed };
      }
      return { isJson: false, data: str };
    } catch (error) {
      return { isJson: false, data: str };
    }
  };

  const formatJsonForDisplay = (obj, indent = 2) => {
    return JSON.stringify(obj, null, indent);
  };

  // One-line label for collapsed JSON, e.g. "order-tracking · 30 orders"
  const jsonSummary = (data) => {
    const parts = [data?.type || data?.subType || (Array.isArray(data) ? "List" : "JSON")];
    const raw = data?.raw ?? data?.data;
    if (Array.isArray(raw?.orders)) parts.push(`${raw.orders.length} orders`);
    else if (Array.isArray(raw?.vouchers)) parts.push(`${raw.vouchers.length} vouchers`);
    else if (Array.isArray(raw)) parts.push(`${raw.length} items`);
    else if (Array.isArray(data)) parts.push(`${data.length} items`);
    else if (raw?.number) parts.push(raw.number);
    if (raw?.status === "error") parts.push("error");
    return parts.join(" · ");
  };

  const toggleJson = (index) => {
    setExpandedJson((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const jsonIndexes = selectedMessages
    .map((m, i) => (safeJsonParse(m.content).isJson ? i : -1))
    .filter((i) => i !== -1);
  const allJsonExpanded = jsonIndexes.length > 0 && jsonIndexes.every((i) => expandedJson.has(i));
  const toggleAllJson = () => setExpandedJson(allJsonExpanded ? new Set() : new Set(jsonIndexes));

  const JsonDisplay = ({ dataString, index }) => {
    const { isJson, data } = safeJsonParse(dataString);

    if (!isJson) {
      return <span className="raw-string">{dataString}</span>;
    }

    const expanded = expandedJson.has(index);
    return (
      <div>
        <button type="button" className="btn btn-sm btn-light py-0 px-2 d-inline-flex align-items-center gap-1" onClick={() => toggleJson(index)} aria-expanded={expanded}>
          <i className={`ri-arrow-${expanded ? "down" : "right"}-s-line`}></i>
          <span>{jsonSummary(data)}</span>
        </button>
        {expanded && (
          <pre className="json-display mt-2 mb-0">
            <code>{formatJsonForDisplay(data)}</code>
          </pre>
        )}
      </div>
    );
  };


  return (
    <Container fluid>
      <h4 className="mb-3">Chat Logs</h4>
      <Row className="mb-4">
        <Col md={3}>
          <Label for="userIdSelect" style={{ fontWeight: "500", fontSize: "14px" }}>Filter by User</Label>
          <Input id="userIdSelect" className="w-100" type="search" list="chatUserOptions" placeholder="All Users — type a user ID or email" value={userSearch} onChange={(e) => userSearchChange(e.target.value)} autoComplete="off" />
          <datalist id="chatUserOptions">
            {users.map((user) => (
              <option key={user._id} value={userLabel(user)} />
            ))}
          </datalist>
        </Col>
        <Col md={3} className="offset-md-3">
          <Label for="sentiment" style={{ fontWeight: "500", fontSize: "14px" }}>Filter by Sentiment</Label>
          <Input id="sentiment" className="w-100" type="select" value={sentiment} onChange={(e) => sentimentChange(e.target.value)}>
            <option value="">Show All</option>
            <option value="happy">Happy</option>
            <option value="neutral">Neutral</option>
            <option value="sad">Sad</option>
          </Input>
        </Col>
        <Col md={3}>
          <Label for="selectedDate" style={{ fontWeight: "500", fontSize: "14px" }}>Filter by Date</Label>
          <Flatpickr disabled={loading} ref={datePickerRef} id="selectedDate" className="form-control d-block bg-white" placeholder="Select Date Range" options={{ mode: "range", dateFormat: "d-m-Y", maxDate: new Date() }} onClose={filterByDate} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th className="px-4">User</th>
                    <th className="px-2">Email</th>
                    <th className="px-2">Resolved</th>
                    <th className="px-2">Overall Sentiment</th>
                    <th className="px-2">Session</th>
                    <th className="px-2">Created</th>
                    <th className="px-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    loading ?
                      [...Array(10)].map((a, i) => (
                        <tr key={i}>
                          <td className="px-4"><Skeleton height={16} width={80} /></td>
                          <td className="px-2"><Skeleton height={16} width={80} /></td>
                          <td className="px-2"><Skeleton height={16} width={80} /></td>
                          <td className="px-2"><Skeleton height={16} width={100} /></td>
                          <td className="px-2"><Skeleton height={16} width={80} /></td>
                          <td className="px-2"><Skeleton height={16} width={100} /></td>
                          <td className="px-2 text-center"><Skeleton height={32} width={80} /></td>
                        </tr>
                      )) :
                      threads.length > 0 ?
                        threads.map((thread) => (
                          <tr key={thread._id}>
                            <td className="px-4">{thread.userId?.externalId || thread.userId?._id}</td>
                            <td className="px-2">{thread?.userId?.email ?? "-"}</td>
                            <td className="px-2">{thread.resolved || thread.freshChatResolved ? thread.resolved ? "Resolved by AI Bot" : "Resolved by FreshChat" : "Unresolved"}</td>
                            <td className="px-2">{thread.overallSentiment === "happy" ? <span className="text-success d-flex align-items-center gap-2"><i className="ri-emotion-happy-line fs-20"></i> Happy</span> : thread.overallSentiment === "neutral" ? <span className="text-warning d-flex align-items-center gap-2"><i className="ri-emotion-normal-line fs-20"></i> Neutral</span> : <span className="text-danger d-flex align-items-center gap-2"><i className="ri-emotion-unhappy-line fs-20"></i> Sad</span>}</td>
                            <td className="px-2">{thread.durationMs ? `${(thread.durationMs / 1000).toFixed(1)} seconds` : "-"}</td>
                            <td className="px-2">{new Date(thread.createdAt).toLocaleString()}</td>
                            <td className="px-2 text-center">
                              <Button size="md" color="primary" onClick={() => openModal(thread._id)}>View Details</Button>
                            </td>
                          </tr>
                        )) :
                        <tr><td colSpan="7" className="text-center">No threads found.</td></tr>
                  }
                </tbody>
              </Table>
              <TablePagination limit={pagination.limit} page={pagination.page} currentRecords={threads.length} total={pagination.total} loading={loading} onPaginationChange={handlePageChange} />
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Modal Chat View */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(false)}>Chat Details</ModalHeader>
        <ModalBody >
          {messagesLoading && <div className="text-center py-4"><Spinner /></div>}
          {!messagesLoading && !selectedMessages.length && <div className="text-center py-4 text-muted">No messages found.</div>}
          {!messagesLoading && selectedMessages.length > 0 && (
            <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
              {translating && <Spinner size="sm" />}
              {translateError && <small className="text-danger me-auto">{translateError}</small>}
              <Button size="sm" color="primary" outline={translatedTo !== "en"} disabled={translating} onClick={() => translateMessages("en")}>Translate to English</Button>
              <Button size="sm" color="primary" outline={translatedTo !== "ar"} disabled={translating} onClick={() => translateMessages("ar")}>ترجمة إلى العربية</Button>
              {translatedTo && <Button size="sm" color="secondary" outline disabled={translating} onClick={showOriginal}>Show original</Button>}
              {jsonIndexes.length > 0 && <Button size="sm" color="secondary" outline onClick={toggleAllJson}>{allJsonExpanded ? "Collapse all JSON" : "Expand all JSON"}</Button>}
            </div>
          )}
          {selectedMessages.map((msg, index) => {
            const isUser = msg.role === "user";
            // Freshchat agent replies are stored as role "assistant" with source "human"
            const isAgent = !isUser && msg.source === "human";
            return (
              <div key={index} className={`d-flex flex-column ${isUser ? "align-items-end" : "align-items-start"} mb-3`}>
                <small className="text-muted mb-1">{isUser ? "User" : isAgent ? "Agent" : "Bot"}</small>
                <div dir="auto" className={`py-2 px-2 rounded text-white ${isUser ? "bg-secondary" : isAgent ? "bg-primary" : "bg-success"}`} style={{ maxWidth: "75%", whiteSpace: "pre-wrap", fontSize: "14px", fontWeight: "500" }}>
                  <JsonDisplay dataString={translations?.[index] ?? msg.content} index={index}/>
                </div>
              </div>
            );
          })}
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-outline-primary" onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
}
